import {
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import LogoutIcon from 'src/icons/LogoutIcon';
import SettingsIcon from 'src/icons/SettingsIcon';
import TIIMenuBurger from 'src/icons/TII-MenuBurger';
import TIIMenuBurgerBlack from 'src/icons/TII-MenuBurger-Black';
import MenuClose from 'src/icons/TII-MenuClose';
import { useSelector } from 'src/store';

interface NavItem {
  icon?: JSX.Element;
  text: string;
  redirectTo: string;
  visible?: boolean;
}

export const DEPARTMENTS = {
  1: 'Traffic',
  2: 'Roads',
  3: 'Parking'
};

export const SECURITY_CLEARANCE = {
  public: 'Public',
  private: 'Private',
  secret: 'Secret'
};

export const ROLES = {
  1: 'Admin',
  2: 'User',
  3: 'Super Admin',
};

const MainNav: FC<any> = ({ color = '#FFF' }: { color: string }) => {
  const { t } = useTranslation();
  const { userProfile = {} } = useSelector((state) => state.user);
  const metaData = useSelector((state) => state.metadata);
  const admin = userProfile && (userProfile.role_id === 1 || userProfile.role_id === 3);
  const menuItems: NavItem[] = [
    {
      icon: <SettingsIcon viewBox="0 0 20 20" />,
      text: t('nbsMenu.settings'),
      redirectTo: '/admin/contents/new',
      visible: admin,
    },
    {
      icon: <LogoutIcon viewBox="0 0 20 20" />,
      text: t('nbsMenu.logout'),
      redirectTo: '/auth/logout',
    },
  ];

  if (metaData.showSettings === false) {
    menuItems.shift();
  }

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (open) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(null);
  };

  const getUserInfo = () => (
    <>
      <Typography sx={{ color: color ?? '#53565a', fontSize: '0.8125rem' }}>
        Welcome
        {' '}
        {`${userProfile.first_name} ${userProfile.last_name}`}
      </Typography>
      {userProfile.role_id === 3 ? (
        <Typography sx={{ color: color ?? '#53565a', fontSize: '0.625rem !important' }}>
          Super Admin
        </Typography>
      ) : (
        <Typography sx={{ color: color ?? '#53565a', fontSize: '0.625rem !important' }}>
          {`${(userProfile.department_name ?? userProfile.department_id).toString().replaceAll('_', '/')} Dept | ${SECURITY_CLEARANCE[userProfile.security_clearance]} | ${ROLES[userProfile.role_id]}`}
        </Typography>
      )}
    </>
  );

  const getMenuIcon = () => (color === '#FFF' ? <TIIMenuBurger viewBox="0 0 50 50" /> : <TIIMenuBurgerBlack viewBox="0 0 50 50" />);

  return (
    <>
      <IconButton
        aria-label="menu"
        disableRipple
        onClick={handleClick}
        sx={{
          zIndex: 1350,
          marginRight: '10px',
          '& .MuiSvgIcon-root': {
            width: 30,
            height: 30,
          },

          '&:hover': {
            background: 'transparent',
          }
        }}
      >
        {userProfile && (
          <span style={{ marginRight: '0.5rem', color: '#FFF' }}>
            {getUserInfo()}
          </span>
        )}
        {open ? <MenuClose viewBox="0 0 50 50" /> : getMenuIcon()}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        id="burger-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.25)',
            mt: 2,
            borderRadius: '10px',
            border: '1px solid #f3f3f3',
            backgroundColor: '#EDEDED',
            width: 270,
            display: 'flex',
            p: 1,
            flexDirection: 'column',
            gap: 1.25,
            '& .MuiList-root': {
              p: 0,
              '& .MuiListItem-root': {
                p: 0,
                pt: 0.625,
                pb: 0.625,
                '&:hover': {
                  backgroundColor: '#0c62fb',
                  color: '#FFFFFF',
                  paddingLeft: '15px',
                  '& .MuiListItemIcon-root': {
                    '& .MuiSvgIcon-root svg path': {
                      fill: '#FFFFFF',
                    },
                  },
                  '& .MuiTypography-root': {
                    color: '#FFFFFF',
                  },
                },
                '& .MuiListItemIcon-root': {
                  mr: 1.25,
                  '& .MuiSvgIcon-root': {
                    width: '20px',
                    height: '20px',
                  },
                },
                '& .MuiTypography-root': {
                  fontSize: '1.125rem',
                },
              },
              '& .MuiDivider-root': {
                mt: 0.625,
                mb: 0.625,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {menuItems.map((item, idx) => {
          const { icon = null, text, redirectTo, visible = true } = item;
          const visibleCount = menuItems.filter(
            (filteredItem) => filteredItem.visible !== false
          ).length;
          return [
            visibleCount > 1 && idx === menuItems.length - 1 && <Divider />,
            visible && (
              <MenuItem component={Link} to={redirectTo}>
                {icon && <ListItemIcon>{icon}</ListItemIcon>}
                <Typography component="span" color="primary.dark" sx={{ fontSize: '18px', fontStyle: 'normal', fontWeight: 400 }}>
                  {text}
                </Typography>
              </MenuItem>
            ),
          ];
        })}
        {/* {userProfile && [
          <Divider key="div" />,
          <MenuItem disabled key="profile-name" sx={{ opacity: '1 !important', color: '#53565a', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
            {getUserInfo()}
          </MenuItem>
        ]} */}
      </Menu>
    </>
  );
};

export default MainNav;
